<app-banner-card [breadcrumbs]="breadcrumbs" />

<app-content-container classes="max-w-[912px]">
  <div class="flex items-center justify-between gap-2 mb-8">
    <div class="flex items-center gap-4">
      <button mat-icon-button color="secondary-transparent" (click)="goBackToCampaigns()">
        <app-icon src="icons/arrow-left.svg" class="w-4 h-4" />
      </button>

      <div class="flex items-center gap-3">
        @if (store.campaignWithCustomerLoading$()) {
          <ngx-skeleton-loader class="h-10" [theme]="{ width: '40px', height: '40px', 'border-radius': '8px' }" />
          <ngx-skeleton-loader class="h-6" [theme]="{ width: '100px', height: '24px', 'border-radius': '4px' }" />
          <div class="flex items-center justify-center w-6 h-6">
            <ngx-skeleton-loader class="h-4" [theme]="{ width: '16px', height: '16px', 'border-radius': '100vw' }" />
          </div>
        } @else {
          <app-icon-badge
            [content]="campaign()?.iconId"
            [fallbackText]="campaign()?.name"
            [size]="40"
            [iconSize]="40"
            [fontSize]="24"
          />
          <h2
            matTooltip
            class="text-2xl font-medium line-clamp-1 text-ellipsis max-w-[340px]"
            [appTextClampTooltip]="campaign()?.name ?? ''"
          >
            {{ campaign()?.name }}
          </h2>

          @if (campaignWithAllLoading()) {
            <div class="flex items-center justify-center w-6 h-6">
              <ngx-skeleton-loader class="h-4" [theme]="{ width: '16px', height: '16px', 'border-radius': '100vw' }" />
            </div>
          } @else {
            @let campaignNameEditDisabled =
              campaign()?.status === ECampaignStatus.ended || expandedSectionId() !== null || !campaignFullyLoaded();

            <div
              class="flex items-center"
              matTooltipPosition="above"
              [matTooltip]="'pages.campaignForm.sections.cannotUpdateEndedCampaign' | translate"
              [matTooltipDisabled]="campaign()?.status !== ECampaignStatus.ended"
            >
              <button
                mat-icon-button
                color="secondary-transparent"
                [disabled]="campaignNameEditDisabled"
                (click)="onEditCampaign()"
              >
                <app-icon src="icons/edit.svg" class="w-4 h-4" />
              </button>
            </div>
          }
        }
      </div>

      @if (store.campaignWithCustomerLoading$()) {
        <ngx-skeleton-loader class="h-6" [theme]="{ width: '52px', height: '24px', 'border-radius': '100vw' }" />
      } @else {
        <app-status-badge [status]="campaign()?.status ?? ECampaignStatus.draft" />
      }
    </div>

    @if (campaignWithAllLoading()) {
      <ngx-skeleton-loader class="h-11" [theme]="{ width: '146px', height: '44px', 'border-radius': '100vw' }" />
    } @else {
      <div
        matTooltipPosition="above"
        [matTooltip]="
          campaign()?.status === ECampaignStatus.ended
            ? ('pages.campaignForm.sections.cannotUpdateEndedCampaign' | translate)
            : ('pages.campaignForm.buttons.allSectionsMustBeCompleted' | translate)
        "
        [matTooltipDisabled]="!startButtonDisabled()"
      >
        <button mat-flat-button [disabled]="startButtonDisabled()" (click)="onStartCampaignClick()">
          @if (redirectingToCampaignDetails() && submitType() === ECampaignFormSubmitType.start) {
            <app-dots-spinner [absoluteCentered]="true" />
          } @else {
            <span [class.hidden]="redirectingToCampaignDetails() && submitType() === ECampaignFormSubmitType.start">
              @if (campaign()?.status === ECampaignStatus.draft) {
                {{ 'pages.campaignForm.buttons.startCampaign' | translate }}
              } @else {
                {{ 'pages.campaignForm.buttons.saveCampaign' | translate }}
              }
            </span>
          }
        </button>
      </div>
    }
  </div>

  <div class="bg-shikenso-gray-base rounded-3xl custom-form">
    <mat-accordion multi>
      <app-campaign-form-expansion-panel
        [class.cursor-progress]="store.campaignSubmitLoading$()"
        [changed]="sectionChanged()"
        [section]="ECampaignFormSection.basics"
        [expanded]="basicsExpanded()"
        [disabled]="basicsDisabled()"
        [submitting]="store.campaignSubmitLoading$()"
        [loading]="campaignWithAllLoading()"
        [campaignWithCustomerLoading]="store.campaignWithCustomerLoading$()"
        [canSave]="canSaveSection() && campaignFullyLoaded()"
        [canSaveDisabledMessage]="canSaveDisabledMessage()"
        [sectionCompleted]="campaign()?.section1Completed ?? false"
        [campaignStatus]="campaign()?.status ?? null"
        (sectionManage)="onSectionManageClick(ECampaignFormSection.basics)"
        (sectionClose)="onSectionCloseClick($event)"
        (sectionSave)="onSectionSaveClick($event)"
      >
        <app-campaign-basics
          [class.pointer-events-none]="store.campaignSubmitLoading$()"
          [campaign]="campaign()"
          [getValues]="getValues()"
          (canSave)="
            updateCanSaveSection(expandedSectionId() === ECampaignFormSection.basics ? $event : canSaveSection())
          "
          (changed)="
            sectionChanged.set(expandedSectionId() === ECampaignFormSection.basics ? $event : sectionChanged())
          "
          (values)="basicsValues.set($event)"
        />
      </app-campaign-form-expansion-panel>

      @if (!basicsExpanded() && !creatorsAndCtasExpanded()) {
        <hr class="border-shikenso-purple-base-12" />
      }

      <app-campaign-form-expansion-panel
        [class.cursor-progress]="store.campaignSubmitLoading$()"
        [changed]="sectionChanged()"
        [section]="ECampaignFormSection.creatorsAndCtas"
        [expanded]="creatorsAndCtasExpanded()"
        [disabled]="creatorsAndCtasDisabled()"
        [submitting]="store.campaignSubmitLoading$()"
        [loading]="campaignWithAllLoading()"
        [campaignWithCustomerLoading]="store.campaignWithCustomerLoading$()"
        [canSave]="canSaveSection() && campaignFullyLoaded()"
        [canSaveDisabledMessage]="canSaveDisabledMessage()"
        [sectionCompleted]="campaign()?.section2Completed ?? false"
        [campaignStatus]="campaign()?.status ?? null"
        (sectionManage)="onSectionManageClick(ECampaignFormSection.creatorsAndCtas)"
        (sectionClose)="onSectionCloseClick($event)"
        (sectionSave)="onSectionSaveClick($event)"
      >
        <app-creators-and-ctas
          [class.pointer-events-none]="store.campaignSubmitLoading$()"
          [campaign]="campaign()"
          [getValues]="getValues()"
          (canSave)="
            updateCanSaveSection(
              expandedSectionId() === ECampaignFormSection.creatorsAndCtas ? $event : canSaveSection()
            )
          "
          (changed)="
            sectionChanged.set(expandedSectionId() === ECampaignFormSection.creatorsAndCtas ? $event : sectionChanged())
          "
          (values)="creatorsAndCtasValues.set($event)"
        />
      </app-campaign-form-expansion-panel>

      @if (!keywordTrackingExpanded() && !creatorsAndCtasExpanded()) {
        <hr class="border-shikenso-purple-base-12" />
      }

      <app-campaign-form-expansion-panel
        [class.cursor-progress]="store.campaignSubmitLoading$()"
        [changed]="sectionChanged()"
        [section]="ECampaignFormSection.keywordTracking"
        [expanded]="keywordTrackingExpanded()"
        [disabled]="keywordTrackingDisabled()"
        [submitting]="store.campaignSubmitLoading$()"
        [loading]="campaignWithAllLoading()"
        [campaignWithCustomerLoading]="store.campaignWithCustomerLoading$()"
        [canSave]="canSaveSection() && campaignFullyLoaded()"
        [canSaveDisabledMessage]="canSaveDisabledMessage()"
        [sectionCompleted]="campaign()?.section3Completed ?? false"
        [campaignStatus]="campaign()?.status ?? null"
        (sectionManage)="onSectionManageClick(ECampaignFormSection.keywordTracking)"
        (sectionClose)="onSectionCloseClick($event)"
        (sectionSave)="onSectionSaveClick($event)"
      >
        <app-keyword-tracking
          [class.pointer-events-none]="store.campaignSubmitLoading$()"
          [campaign]="campaign()"
          [getValues]="getValues()"
          (canSave)="
            updateCanSaveSection(
              expandedSectionId() === ECampaignFormSection.keywordTracking ? $event : canSaveSection()
            )
          "
          (changed)="
            sectionChanged.set(expandedSectionId() === ECampaignFormSection.keywordTracking ? $event : sectionChanged())
          "
          (values)="keywordTrackingValues.set($event)"
        />
      </app-campaign-form-expansion-panel>
    </mat-accordion>
  </div>
</app-content-container>
